import { scopedT } from 'utils/functions/Translations';

type Options = Array<{
  columnLabel?: string;
  displayLabel?: string;
  label: string;
  value: string;
}>;

const t = scopedT();

const getEstOrActSortOptions = (
  columnLabelKey: 'arrivalTime' | 'departureTime' | 'dischargeTime',
  displayLabelKey:
    | 'sortArrivalTimeOption'
    | 'sortDepartureTimeOption'
    | 'dischargeTimeOption',
  sortByOption:
    | 'estimated_actual_arrival'
    | 'estimated_actual_departure'
    | 'poo_estimated_actual_departure'
    | 'pod_estimated_actual_arrival',
) => {
  return [
    {
      columnLabel: t(`sort.${columnLabelKey}`),
      displayLabel: t(`sort.${displayLabelKey}`, {
        option: t('sort.latestToEarliest'),
      }),
      label: t('sort.latestToEarliest'),
      value: `${sortByOption}/desc`,
    },
    {
      columnLabel: t(`sort.${columnLabelKey}`),
      displayLabel: t(`sort.${displayLabelKey}`, {
        option: t('sort.earliestToLatest'),
      }),
      value: `${sortByOption}/asc`,
      label: t('sort.earliestToLatest'),
    },
    {
      columnLabel: t(`sort.${columnLabelKey}`),
      displayLabel: t(`sort.${displayLabelKey}`, {
        option: t('sort.mostDelayed'),
      }),
      label: t('sort.mostDelayed'),
      value: `${sortByOption}_delay/desc`,
    },
    {
      columnLabel: t(`sort.${columnLabelKey}`),
      displayLabel: t(`sort.${displayLabelKey}`, {
        option: t('sort.leastDelayed'),
      }),
      value: `${sortByOption}_delay/asc`,
      label: t('sort.leastDelayed'),
    },
  ];
};

const getPlannedSortOptions = (
  columnLabelKey: 'plannedDeparture' | 'plannedArrival' | 'plannedDischarge',
  displayLabelKey:
    | 'plannedDepartureOption'
    | 'plannedArrivalOption'
    | 'plannedDischargeOption',
  sortByOption:
    | 'planned_departure'
    | 'planned_arrival'
    | 'poo_planned_departure'
    | 'pod_planned_arrival',
) => {
  return [
    {
      columnLabel: t(`sort.${columnLabelKey}`),
      displayLabel: t(`sort.${displayLabelKey}`, {
        option: t('sort.earliest'),
      }),
      label: t('sort.earliestToLatest'),
      value: `${sortByOption}/asc`,
    },
    {
      columnLabel: t(`sort.${columnLabelKey}`),
      displayLabel: t(`sort.${displayLabelKey}`, {
        option: t('sort.latest'),
      }),
      value: `${sortByOption}/desc`,
      label: t('sort.latestToEarliest'),
    },
  ];
};

export const ARRIVAL_SORT_OPTIONS = getEstOrActSortOptions(
  'arrivalTime',
  'sortArrivalTimeOption',
  'estimated_actual_arrival',
);

export const POD_ARRIVAL_SORT_OPTIONS = getEstOrActSortOptions(
  'dischargeTime',
  'dischargeTimeOption',
  'pod_estimated_actual_arrival',
);

export const DEPARTURE_SORT_OPTIONS = getEstOrActSortOptions(
  'departureTime',
  'sortDepartureTimeOption',
  'estimated_actual_departure',
);

export const POO_DEPARTURE_SORT_OPTIONS = getEstOrActSortOptions(
  'departureTime',
  'sortDepartureTimeOption',
  'poo_estimated_actual_departure',
);

export const PLANNED_SORT_OPTIONS_ARRIVAL = getPlannedSortOptions(
  'plannedArrival',
  'plannedArrivalOption',
  'planned_arrival',
);

export const PLANNED_SORT_OPTIONS_DEPARTURE = getPlannedSortOptions(
  'plannedDeparture',
  'plannedDepartureOption',
  'planned_departure',
);

export const POO_PLANNED_SORT_OPTIONS_DEPARTURE = getPlannedSortOptions(
  'plannedDeparture',
  'plannedDepartureOption',
  'poo_planned_departure',
);

export const POD_PLANNED_SORT_OPTIONS_ARRIVAL = getPlannedSortOptions(
  'plannedDischarge',
  'plannedDischargeOption',
  'pod_planned_arrival',
);

export const CREATED_TIME_SORT_OPTIONS: Options = [
  {
    displayLabel: t('sort.mostRecentlyOption', { option: t('sort.created') }),
    label: t('sort.mostRecentlyOption', { option: t('sort.created') }),
    value: 'recently_created/desc',
  },
  {
    displayLabel: t('sort.leastRecentlyOption', { option: t('sort.created') }),
    label: t('sort.leastRecentlyOption', { option: t('sort.created') }),
    value: 'recently_created/asc',
  },
];

export const LAST_MODIFIED_TIME_SORT_OPTIONS: Options = [
  {
    displayLabel: t('sort.mostRecentlyOption', { option: t('sort.updated') }),
    label: t('sort.mostRecentlyOption', { option: t('sort.updated') }),
    value: 'recently_updated/desc',
  },
  {
    displayLabel: t('sort.leastRecentlyOption', { option: t('sort.updated') }),
    label: t('sort.leastRecentlyOption', { option: t('sort.updated') }),
    value: 'recently_updated/asc',
  },
];

export const ORDER_SUBMISSION_OPTIONS: Options = [
  {
    label: t('sort.leastRecentlyOption', { option: t('sort.submitted') }),
    displayLabel: t('sort.leastRecentlyOption', {
      option: t('sort.submitted'),
    }),
    value: 'orderSubmissionDateTime/asc',
  },
  {
    label: t('sort.mostRecentlyOption', { option: t('sort.submitted') }),
    value: 'orderSubmissionDateTime/desc',
    displayLabel: t('sort.mostRecentlyOption', { option: t('sort.submitted') }),
  },
];

export const ORDER_READY_OPTIONS = [
  {
    columnLabel: t('sort.supplierReadyDate'),
    displayLabel: t('sort.supplierReadyOption', { option: t('sort.earliest') }),
    label: t('sort.earliestToLatest'),
    value: 'supplierReadyDateTimeWindow.endDateTime/asc',
  },
  {
    columnLabel: t('sort.supplierReadyDate'),
    displayLabel: t('sort.supplierReadyOption', { option: t('sort.latest') }),
    label: t('sort.latestToEarliest'),
    value: 'supplierReadyDateTimeWindow.endDateTime/desc',
  },
];

export const ORDER_DELIVERY_OPTIONS = [
  {
    columnLabel: t('sort.orderDueDate'),
    displayLabel: t('sort.orderDueDateOption', { option: t('sort.earliest') }),
    label: t('sort.earliestToLatest'),
    value: 'originalDeliveryDateTimeWindow.endDateTime/asc',
  },
  {
    columnLabel: t('sort.orderDueDate'),
    displayLabel: t('sort.orderDueDateOption', { option: t('sort.latest') }),
    label: t('sort.latestToEarliest'),
    value: 'originalDeliveryDateTimeWindow.endDateTime/desc',
  },
];

export const CORE_SORT_OPTIONS: Options = [
  {
    label: t('sort.mostRecentlyOption', { option: t('sort.updated') }),
    value: 'recently_updated/desc',
  },
  {
    label: t('sort.leastRecentlyOption', { option: t('sort.updated') }),
    value: 'recently_updated/asc',
  },
  {
    label: t('sort.mostRecentlyOption', { option: t('sort.created') }),
    value: 'recently_created/desc',
  },
  {
    label: t('sort.leastRecentlyOption', { option: t('sort.created') }),
    value: 'recently_created/asc',
  },
];

export const MASTER_SORT_OPTIONS = [
  ...ARRIVAL_SORT_OPTIONS,
  ...CORE_SORT_OPTIONS,
  ...DEPARTURE_SORT_OPTIONS,
  ...PLANNED_SORT_OPTIONS_ARRIVAL,
  ...PLANNED_SORT_OPTIONS_DEPARTURE,
  ...ORDER_SUBMISSION_OPTIONS,
  ...ORDER_READY_OPTIONS,
  ...ORDER_DELIVERY_OPTIONS,
  ...POO_DEPARTURE_SORT_OPTIONS,
  ...POO_PLANNED_SORT_OPTIONS_DEPARTURE,
  ...POD_ARRIVAL_SORT_OPTIONS,
  ...POD_PLANNED_SORT_OPTIONS_ARRIVAL,
  ...CREATED_TIME_SORT_OPTIONS,
  ...LAST_MODIFIED_TIME_SORT_OPTIONS,
];
